import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import { permissionChecks } from '../../../../utils/permission-check';
import { hasElements } from '../../../../utils/variables';
import { ProjectGeodataManager } from '../../../../viewer/project-geodata-manager';
import { hasContainerData } from '../../../../viewer/timeline-manager';
import { ViewSettings } from '../../../../viewer/view-settings';
import { viewerZIndices } from '../../../../viewer/viewer-z-indices';
import { useActiveTimelineContainer } from '../../../hooks/use-active-timeline-container';
import { useFeatureFlag } from '../../../hooks/use-feature-flag';
import { SkyviewContext } from '../../skyview/skyview-state';
import { SnappingDialog } from '../components/snapping-dialog/snapping-dialog';
import { ToolIcon } from './tool-icon';
import { ToolbarGroup } from './toolbar-group';
import { ClosePanoramaViewerToolIcon } from './tools/close-panorama-viewer-tool-icon';
import { CompareOrthophotosToolIcon } from './tools/compare-orthophotos-tool-icon';
import { DesignToolIcon } from './tools/design-tool-icon';
import { FocusToolIcon } from './tools/focus-tool-icon';
import { InspectOriginalPhotosToolIcon } from './tools/inspect-original-photos-tool-icon';
import { IntersectionToolIcon } from './tools/intersection-tool-icon';
import { LightToolIcon } from './tools/light/light-tool-icon';
import { MeasurementAdvancedToolIcon } from './tools/measurement-advanced-tool-icon';
import { MeasurementToolIcon } from './tools/measurement-tool-icon';
import { OcclusionCullingToolIcon } from './tools/occlusion-culling-tool-icon';
import { OrthophotoTool } from './tools/orthophoto-tool';
import { PerspectiveTool } from './tools/perspective-tool';
import { PoiToolIcon } from './tools/poi-tool-icon';
import { PointCloudTool } from './tools/point-cloud/point-cloud-tool';
import { ProfileSectionToolIcon } from './tools/profile-section-tool-icon';
import { ReferencePlaneToolIcon } from './tools/reference-plane/reference-plane-tool-icon';
import { ReportToolIcon } from './tools/report-tool-icon';
import { Rotate360PoiTowardsNorthToolIcon } from './tools/rotate-360-poi-towards-north-tool-icon';
import { RotateToolIcon } from './tools/rotate-tool-icon';
import { ScaleToolIcon } from './tools/scale-tool-icon';
import { SelectToolIcon } from './tools/select-tool-icon';
import { Timeline } from './tools/timeline/timeline';
import { TranslateToolIcon } from './tools/translate-tool-icon';
import { FlexibleCameraTool } from './tools/view-settings/flexible-camera-tool';
import {
  useSceneFollowsViewSettingsFlag,
  useViewSettings,
} from './tools/view-settings/use-view-settings';
import { ViewSettingsTool } from './tools/view-settings/view-settings-tool';
import { VolumeCalculationToolIcon } from './tools/volume-calculation-tool-icon';
import { WmsToolIcon } from './tools/wms-tool-icon';

const Toolbar = () => {
  const { activeScene, skyview } = useContext(SkyviewContext);
  // Re-renders when the viewing mode changes, also when the view settings are turned on or off.
  useViewSettings();
  useFeatureFlag('SK-7549-view-settings');
  // The timeline bar at the top takes the timeline's place
  const timelineBarEnabled = useFeatureFlag('SK-7562-timeline-bar');
  useSceneFollowsViewSettingsFlag();
  const activeTimelineContainer = useActiveTimelineContainer();
  const [expanded, setExpanded] = useState(true);
  const ref = React.useRef<HTMLDivElement>(null);

  const isPanoramaViewActive = activeScene === 'panoramaViewer';
  const timelineTools =
    !isPanoramaViewActive && !timelineBarEnabled
      ? [
          permissionChecks.hasAccessToFeature('feature:viewer:toolbar:timeline') &&
            activeTimelineContainer && (
              <Timeline
                currentTimelineContainer={activeTimelineContainer}
                maxVisibleItems={5}
                timelineContainers={ProjectGeodataManager.instance()
                  .getTimelineContainers()
                  .reverse()}
              />
            ),
        ].filter(Boolean)
      : [];

  const isNotSharedView = () => skyview.sharedView !== true;

  const hasTiledModelSceneAccess = permissionChecks.hasAccessToFeature(
    'feature:viewer:toolbar:tiled_model_scene',
  );
  const hasOrthoSceneAccess = permissionChecks.hasAccessToFeature(
    'feature:viewer:toolbar:ortho_tile_scene',
  );
  const hasPointCloudSceneAccess = permissionChecks.hasAccessToFeature(
    'feature:viewer:toolbar:point_cloud_scene',
  );
  // A timeline container without data has no classic view to show, so the cameras take their place
  const hasNoTimelineData = !!activeTimelineContainer && !hasContainerData(activeTimelineContainer);
  // The CAD viewer and the flexible viewing mode show the cameras and the view settings below the
  // orientation cube
  const viewTools = (
    ViewSettings.instance.showsOrientationCube
      ? []
      : hasNoTimelineData
        ? [
            (hasTiledModelSceneAccess || hasPointCloudSceneAccess) && (
              <FlexibleCameraTool camera="perspective" key="flexiblePerspectiveTool" />
            ),
            hasOrthoSceneAccess && (
              <FlexibleCameraTool camera="orthographic" key="flexibleOrthographicTool" />
            ),
          ]
        : [
            hasTiledModelSceneAccess && <PerspectiveTool key="perspectiveTool" />,
            hasOrthoSceneAccess && <OrthophotoTool key="orthophotoTool" />,
            hasPointCloudSceneAccess && (
              <PointCloudTool
                key="pointCloudTool"
                toolbarRef={ref}
                withSettings={!ViewSettings.instance.isEnabled}
              />
            ),
          ]
  ).filter(Boolean);
  // The flexible viewing mode has the view settings by the orientation cube too, and the CAD viewer
  // has no layers to show
  if (ViewSettings.instance.isEnabled && hasElements(viewTools)) {
    viewTools.push(<ViewSettingsTool key="viewSettingsTool" toolbarRef={ref} />);
  }

  const transformationTools = !isPanoramaViewActive
    ? [
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:select') && (
          <SelectToolIcon key="select-tool-icon" />
        ),
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:translate', [
          isNotSharedView,
        ]) && <TranslateToolIcon key="translate-tool-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:rotate', [isNotSharedView]) && (
          <RotateToolIcon key="rotate-tool-icon" />
        ),
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:scale', [isNotSharedView]) && (
          <ScaleToolIcon key="scale-tool-icon" />
        ),
        // By the orientation cube where it is shown
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:focus', [
          () => !skyview.isCadViewerActive(),
          () => !ViewSettings.instance.showsOrientationCube,
        ]) && <FocusToolIcon key="focus-tool-icon" />,
      ].filter(Boolean)
    : [];

  const poiTool = <PoiToolIcon key="poi-tool-icon" />;
  const mainTools = !isPanoramaViewActive
    ? [
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:comment', [isNotSharedView]) &&
          poiTool,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:design', [isNotSharedView]) && (
          <DesignToolIcon key="design-tool-icon" />
        ),
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:measurement', [
          () => !skyview.isCadViewerActive(),
        ]) && <MeasurementToolIcon key="measurement-tool-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:measurement:advanced', [
          () => !skyview.isCadViewerActive(),
        ]) && <MeasurementAdvancedToolIcon key="measurement-tool-advanced-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:volume_calculation', [
          isNotSharedView,
        ]) && <VolumeCalculationToolIcon key="volume-calculation-tool-icon" />,
      ].filter(Boolean)
    : [];

  const analyzeTools = !isPanoramaViewActive
    ? [
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:original_photos', [
          () => !skyview.isCadViewerActive(),
          isNotSharedView,
        ]) && <InspectOriginalPhotosToolIcon key="inspect-original-photos-tool-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:profile_section', [
          isNotSharedView,
        ]) && <ProfileSectionToolIcon key="profile-section-tool-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:compare_ortho_photos', [
          () => !skyview.isCadViewerActive(),
          isNotSharedView,
        ]) && <CompareOrthophotosToolIcon key="compare-orthophotos-tool-advanced-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:volume_calculation', [
          isNotSharedView,
        ]) && <ReportToolIcon key="report-tool-icon" />,
      ].filter(Boolean)
    : [];

  const wmsTools = !isPanoramaViewActive
    ? [
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:wms') && (
          <WmsToolIcon key="wms-tool-icon" />
        ),
      ].filter(Boolean)
    : [];

  const lightTools = !isPanoramaViewActive
    ? [
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:intersection') && (
          <IntersectionToolIcon key="intersection-tool-icon" />
        ),
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:occlusion_culling', [
          () => !skyview.isCadViewerActive(),
        ]) && <OcclusionCullingToolIcon key="occlusion-culling-tool-icon" />,
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:light') && (
          <LightToolIcon key="light-tool-icon" toolbarRef={ref} />
        ),
        permissionChecks.hasAccessToFeature('feature:viewer:toolbar:reference_plane', [
          () => !skyview.isCadViewerActive(),
        ]) && <ReferencePlaneToolIcon key="reference-plane-icon" toolbarRef={ref} />,
      ].filter(Boolean)
    : [];

  const panoramaTools = isPanoramaViewActive
    ? [
        <Rotate360PoiTowardsNorthToolIcon key="orient-360-poi-to-north-tool-icon" />,
        <ClosePanoramaViewerToolIcon key="close-panorama-viewer-tool-icon" />,
      ].filter(Boolean)
    : [];

  return (
    <Component expanded={expanded}>
      <Positioned expanded={expanded} ref={ref}>
        {expanded && (
          <>
            {hasElements(timelineTools) && <ToolbarGroup>{timelineTools}</ToolbarGroup>}
            {hasElements(viewTools) && <ToolbarGroup>{viewTools}</ToolbarGroup>}
            {hasElements(transformationTools) && <ToolbarGroup>{transformationTools}</ToolbarGroup>}
            {hasElements(mainTools) && <ToolbarGroup>{mainTools}</ToolbarGroup>}
            {hasElements(analyzeTools) && <ToolbarGroup>{analyzeTools}</ToolbarGroup>}
            {hasElements(wmsTools) && <ToolbarGroup>{wmsTools}</ToolbarGroup>}
            {hasElements(lightTools) && <ToolbarGroup>{lightTools}</ToolbarGroup>}
            {hasElements(panoramaTools) && <ToolbarGroup>{panoramaTools}</ToolbarGroup>}

            {!isPanoramaViewActive && (
              <CollapseButtonGroup>
                <ToolIcon
                  active={false}
                  callback={() => setExpanded(false)}
                  icon={'chevron-left'}
                  key={'collapse-icon'}
                />
              </CollapseButtonGroup>
            )}
          </>
        )}

        {!expanded && (
          <>
            <ToolbarGroup>
              <ToolIcon
                active={false}
                callback={() => setExpanded(true)}
                icon={'chevron-right'}
                key={'collapse-icon'}
              />
            </ToolbarGroup>
          </>
        )}
      </Positioned>
      {!isPanoramaViewActive && <SnappingDialog />}
    </Component>
  );
};

const Component = styled.div<{ expanded: boolean }>`
  display: flex;
  justify-content: ${(props) => (props.expanded ? 'center' : 'flex-start')};
  margin-top: auto;
  padding: 16px;

  // Makes sure the padding around the toolbar does not prevent clicks.
  pointer-events: none !important;

  z-index: ${viewerZIndices.toolbar};
`;

const Positioned = styled.div<{ expanded: boolean }>`
  pointer-events: auto;
  display: flex;
  flex-wrap: wrap;
  // As round as the timeline bar's panel
  border-radius: ${(props) => (props.expanded ? '14px' : '32px')};

  margin: 0;
  padding: 8px;

  background-color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);

  html[data-intercom='true'] & {
    margin-right: 100px;
  }
`;

const CollapseButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
`;

export { Toolbar };
